"use client";

import React from 'react';
import { useTranslations } from 'next-intl';
import {
    Container,
    Typography
} from '@/components/design-system';
import { Zap, Database, Shield } from 'lucide-react';
import HomeCategories from './_components/HomeCategories';
import HomeGenerators from './_components/HomeGenerators';
import HomeDataGenerators from './_components/HomeDataGenerators';

export default function HomePage() {
    const t = useTranslations('homepage');
    
    return (
        <div id="homepage" className="min-h-screen">
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-gradient-to-br from-background via-background to-muted/20 min-h-[90vh] flex items-center">
                {/* Animated background elements */}
                <div className="absolute inset-0">
                    <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-primary/20 to-transparent rounded-full blur-3xl animate-pulse" />
                    <div className="absolute top-40 right-20 w-96 h-96 bg-gradient-to-br from-secondary/20 to-transparent rounded-full blur-3xl animate-pulse delay-1000" />
                    <div className="absolute bottom-20 left-1/3 w-64 h-64 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-3xl animate-pulse delay-2000" />
                </div>

                {/* Floating particles */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-primary/30 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
                    <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-secondary/40 rounded-full animate-bounce" style={{ animationDelay: '1s' }} />
                    <div className="absolute bottom-1/3 left-1/2 w-1.5 h-1.5 bg-primary/20 rounded-full animate-bounce" style={{ animationDelay: '2s' }} />
                    <div className="absolute top-2/3 right-1/4 w-1 h-1 bg-secondary/30 rounded-full animate-bounce" style={{ animationDelay: '3s' }} />
                </div>

                <Container className="relative z-10">
                    <div className="py-20">
                        <div className="max-w-5xl mx-auto text-center space-y-12">
                            {/* Main heading */}
                            <div className="space-y-8 animate-in fade-in-0 duration-1000 delay-300">
                                <Typography
                                    variant="h1"
                                    className="text-6xl md:text-7xl lg:text-8xl font-bold bg-gradient-to-r from-foreground via-foreground/90 to-foreground/70 bg-clip-text text-transparent leading-tight tracking-tight"
                                >
                                    {t('hero.title')}
                                    <span className="block bg-gradient-to-r from-primary via-primary/90 to-primary/70 bg-clip-text text-transparent">
                                        {t('hero.subtitle')}
                                    </span>
                                </Typography>

                                <Typography variant="lead" className="max-w-3xl mx-auto text-xl text-muted-foreground leading-relaxed">
                                    {t('hero.description')}
                                    <span className="font-semibold text-foreground"> {t('hero.noRegistration')}</span>
                                </Typography>
                            </div>

                            {/* Feature highlights */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto animate-in fade-in-0 duration-1000 delay-700">
                                <div className="flex flex-col items-center gap-4 p-6 rounded-2xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 hover:border-primary/30 transition-all duration-300 group">
                                    <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                                        <Zap className="h-6 w-6 text-primary" />
                                    </div>
                                    <div className="text-center">
                                        <Typography variant="h4" className="font-semibold mb-2">{t('features.lightningFast.title')}</Typography>
                                        <Typography variant="muted" className="text-sm">{t('features.lightningFast.description')}</Typography>
                                    </div>
                                </div>
                                <div className="flex flex-col items-center gap-4 p-6 rounded-2xl bg-gradient-to-br from-secondary/5 to-secondary/10 border border-secondary/20 hover:border-secondary/30 transition-all duration-300 group">
                                    <div className="w-12 h-12 bg-secondary/20 rounded-xl flex items-center justify-center group-hover:bg-secondary/30 transition-colors">
                                        <Database className="h-6 w-6 text-secondary" />
                                    </div>
                                    <div className="text-center">
                                        <Typography variant="h4" className="font-semibold mb-2">{t('features.richDataTypes.title')}</Typography>
                                        <Typography variant="muted" className="text-sm">{t('features.richDataTypes.description')}</Typography>
                                    </div>
                                </div>
                                <div className="flex flex-col items-center gap-4 p-6 rounded-2xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 hover:border-primary/30 transition-all duration-300 group">
                                    <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                                        <Shield className="h-6 w-6 text-primary" />
                                    </div>
                                    <div className="text-center">
                                        <Typography variant="h4" className="font-semibold mb-2">{t('features.privacyFirst.title')}</Typography>
                                        <Typography variant="muted" className="text-sm">{t('features.privacyFirst.description')}</Typography>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>

            {/* Categories Section */}
            <HomeCategories />

            {/* Popular Generators Section */}
            <HomeGenerators />

            {/* Popular Data Generators Section */}
            <HomeDataGenerators />
        </div>
    );
}
